import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BASE_URL_API } from "../Constants";
import "../Components/modal.css";
import "../Pages/Permissions.css";
import { normalizeRoleListRow } from "../utils/listRowIds";
import { LanguageContext } from "../context/LanguageContext";
import permissionTranslations from "../languages/permission.json";

const SYSTEM_ROLE_NAMES = new Set(["super", "owner"]);
const isSystemRoleName = (name) =>
  SYSTEM_ROLE_NAMES.has(String(name || "").trim().toLowerCase());

export default function PermissionsModal({
  permission,
  onClose,
  showAlert,
  hideSystemRoles = false,
  lockSystemRoles = true,
  stagedRoleIds = null,
  onStage,
  targetSubid = null
}) {
  const { lang } = useContext(LanguageContext);
  const translations = permissionTranslations[lang] || permissionTranslations.en;
  const t = translations.modal;
  const isRtl = ["ar", "ur"].includes(lang);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const initialSelected = useMemo(() => {
    if (Array.isArray(stagedRoleIds)) return stagedRoleIds.map(Number);

    const r = Array.isArray(permission?.Roles) ? permission.Roles : [];
    return r
      .map((x) => Number(x.role_id || x.roleId || x.id))
      .filter((n) => Number.isFinite(n));
  }, [permission, stagedRoleIds]);

  const systemRoleIds = useMemo(() => {
    return (Array.isArray(roles) ? roles : [])
      .filter((r) => isSystemRoleName(r?.name))
      .map((r) => Number(r?.role_id || r?.id))
      .filter(Number.isFinite);
  }, [roles]);

  const visibleRoles = useMemo(
    () => (hideSystemRoles ? roles.filter((role) => !isSystemRoleName(role?.name)) : roles),
    [hideSystemRoles, roles]
  );

  const enforceSystemSelected = (roleIds) => {
    const next = new Set(
      (Array.isArray(roleIds) ? roleIds : [])
        .map(Number)
        .filter(Number.isFinite)
    );
    if (lockSystemRoles) {
      systemRoleIds.forEach((rid) => next.add(rid));
    }
    return Array.from(next);
  };

  useEffect(() => {
    let mounted = true;

    const loadRoles = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BASE_URL_API}/roles`, {
          params: targetSubid ? { target_subid: targetSubid } : undefined
        });
        const list = res.data?.data || res.data?.roles || [];

        if (!Array.isArray(list)) {
          showAlert?.("error", t.invalidRolesList);
          if (mounted) setRoles([]);
          return;
        }

        if (!mounted) return;
        const normalizedRoles = list.map(normalizeRoleListRow);
        setRoles(normalizedRoles);

        const base = initialSelected;
        const enforced = (() => {
          const sys = normalizedRoles
            .filter((r) => isSystemRoleName(r?.name))
            .map((r) => Number(r?.role_id || r?.id))
            .filter(Number.isFinite);

          const s = new Set(base.map(Number).filter(Number.isFinite));
          sys.forEach((rid) => s.add(rid));
          return Array.from(s);
        })();

        setSelected(enforced);
      } catch {
        showAlert?.("error", t.failedLoadRoles);
        if (mounted) setRoles([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadRoles();
    return () => {
      mounted = false;
    };
  }, [initialSelected, showAlert, t.failedLoadRoles, t.invalidRolesList, targetSubid]);

  const toggleRole = (roleId) => {
    const rid = Number(roleId);

    if (lockSystemRoles && systemRoleIds.includes(rid)) {
      showAlert?.("info", t.systemRolesLocked);
      return;
    }

    setSelected((prev) => {
      const next = prev.includes(rid)
        ? prev.filter((x) => x !== rid)
        : [...prev, rid];

      return enforceSystemSelected(next);
    });
  };

  const handleDone = async () => {
    if (!permission?.id) return;
    setSaving(true);

    try {
      const enforced = enforceSystemSelected(selected);
      onStage?.(Number(permission.id), enforced);
      showAlert?.("success", t.stagedSuccess);
      onClose?.();
    } catch {
      showAlert?.("error", t.stagedError);
    } finally {
      setSaving(false);
    }
  };

  if (!permission) return null;

  return (
    <div id="modal" onClick={onClose} style={{ zIndex: 2000 }} dir={isRtl ? "rtl" : "ltr"}>
      <div
        id="popup"
        className="dashboard-modal permissions-dialog permissions-manage-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dashboard-header">
          <div className="dashboard-header-text">
            <h2>{t.title}</h2>
            <p>
              {t.permissionLabel}: <b>{permission.name}</b>
            </p>
          </div>
        </div>

        <div className="modal-divider" />

        <div className="modal-content dashboard-content">
          {loading ? (
            <div className="permissions-modal-loading">{t.loadingRoles}</div>
          ) : (
            <div className="permissions-modal-role-list">
              {!hideSystemRoles || roles.length === visibleRoles.length ? null : (
                <div className="permissions-modal-note">{t.restrictedRolesNotice}</div>
              )}

              {visibleRoles.map((r) => {
                const rid = Number(r.role_id || r.id);
                const systemRole = isSystemRoleName(r.name);

                const locked = lockSystemRoles && systemRole;
                const checked = locked ? true : selected.includes(rid);

                return (
                  <label
                    key={rid}
                    className={`permissions-modal-role${locked ? " permissions-modal-role-locked" : ""}`}
                    title={locked ? t.systemRoleTooltip : ""}
                  >
                    <span className="permissions-modal-role-info">
                      <span className="permissions-modal-role-name">{r.name}</span>
                      {systemRole ? <span className="permissions-modal-system-badge">{t.systemRoleLabel}</span> : null}
                    </span>

                    <input
                      className="permissions-modal-role-toggle"
                      type="checkbox"
                      checked={checked}
                      disabled={locked}
                      onChange={() => toggleRole(rid)}
                    />
                  </label>
                );
              })}

              {!visibleRoles.length ? (
                <div className="permissions-modal-empty">{t.noRolesAvailable}</div>
              ) : null}
            </div>
          )}
        </div>

        <div className="modal-footer dashboard-footer permissions-modal-footer">
          <div className="modal-footer-right-buttons">
            <button className="btn btn-cancel" type="button" onClick={onClose} disabled={saving}>
              {t.buttonCancel}
            </button>

            <button
              className="btn btn-apply dashboard-primary-btn"
              type="button"
              onClick={handleDone}
              disabled={saving || loading}
            >
              {saving ? t.buttonSaving : t.buttonDone}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
